import React, { useState, useEffect, useRef, Component } from "react";
import { BrowserRouter, Routes, Route, matchPath, useLocation } from 'react-router-dom';
import Login from './pages/login';
import Search from './pages/search';

import StoryAdd from './pages/story/StoryAdd';
import StoryStatistic from './pages/story/StoryStatistic';
import StoryUsers from './pages/story/StoryUsers';
import ReactionLine from './pages/reaction/ReactionLine';
import ReactionEditor from './pages/reaction/ReactionEditor';
import ReactionReport from './pages/reaction/ReactionReport';
import UserProfile from './pages/user/UserProfile';
import NotificationsProvider from './notifications/NotificationsProvider';
import InAppProvider from './inApp/InAppProvider';
import NotificationsPage from './notifications/NotificationsPage';
import UserSettings from './pages/user/UserSettings';
import UserFollower from './pages/user/UserFollower';
import UserControl from './pages/user/UserControl';
import Page404 from './pages/404';
import BrandLoader from './components/Brand/BrandLoader';
import AppFailure from './components/Brand/AppFailure';
import MessageLayout from './components/Brand/MessageLayout';
import Page403 from './pages/403';
import Static from './pages/static';
import AppUrlListener from './components/AppUrlListener';
import AppInstallBanner from './components/AppInstallBanner';
import OnboardingProvider, { useOnboarding } from './onboarding/OnboardingProvider';
import { useTranslation } from "react-i18next";
import config from './utils/config';
import { setLocalStorage, getLocalStorage, removeUser } from './utils/func';
import { APP_ROUTES } from './utils/constant';
import api from './services';
import {Helmet} from "react-helmet";
import { ConfigProvider } from 'antd';
import  varCss from './variable.scss';
import { initGA, logPageView } from './analytics';
import { useViewportCss } from './hooks/useViewport';
import { initialization as translationsReady } from './i18n';
import changeLanguageInOrder from './utils/changeLanguageInOrder';

const { userByToken } = api;
const uiRadius = parseFloat(varCss['ui-radius']) || 4;

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('WIW render error:', error, info); }
  render() { return this.state.error ? <AppFailure /> : this.props.children; }
}

function AppContent() {
  useViewportCss();
  const { ready: entryReady, eligible: onboardingEligible } = useOnboarding();
  const location = useLocation();
  const initialized = useRef(false);
  const loginToken = useRef(undefined);
  const token = getLocalStorage(config.token);
  const authenticatedFromLogin = loginToken.current !== undefined && Boolean(token) && token !== loginToken.current;
  const isLoginRoute = config.languages.some(language =>
    matchPath(`${language ? `/${language}` : ''}${APP_ROUTES.LOGIN}`, location.pathname));
  const [load, setLoad] = useState(true);
  const [initError, setInitError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  // Brand strings are bundled. Do not suspend this component behind the
  // remote translation file: session validation can start at the same time.
  const { t, i18n } = useTranslation('brand', { useSuspense: false });

  useEffect(() => {
    // The Capacitor SDK also creates its global in ordinary browsers.
    const isCapacitor = window.Capacitor?.isNativePlatform?.() === true
      || window.location.protocol === 'capacitor:';
    if (!isCapacitor && !isLoginRoute) { initGA(); logPageView(); }
  }, [isLoginRoute]);

  useEffect(() => {
    if (initialized.current) return;
    // finishSignIn has already validated and installed this complete session
    // and its language. Skip/Back with the old token still needs validation.
    if (!isLoginRoute && authenticatedFromLogin) {
      initialized.current = true;
      setLoad(false);
      setInitError(null);
      return;
    }
    if (isLoginRoute) loginToken.current = getLocalStorage(config.token);
    let cancelled = false;
    const controller = new AbortController();
    const languageInPath = window.location.pathname.split('/')[1];
    const initialLanguage = languageInPath && config.languages.includes(languageInPath) ? languageInPath : 'en';
    const initialize = async () => {
      setLoad(true);
      setInitError(null);
      try {
        const [, response] = await Promise.all([
          translationsReady.then(() => changeLanguageInOrder(i18n, initialLanguage, () => !cancelled)),
          // An OAuth return must reach its handler even if the old session
          // cannot be checked. The login handler installs the new session.
          isLoginRoute ? Promise.resolve(null) : userByToken(undefined, { signal: controller.signal }),
        ]);
        if (cancelled) return;
        if (!isLoginRoute && !response?.success) throw new Error('User initialization unavailable');
        if (response?.user) {
          setLocalStorage(config.user, JSON.stringify(response.user));
          const preferredLanguage = response.user.lng_code;
          if (preferredLanguage !== initialLanguage &&
            (preferredLanguage === 'en' || config.languages.includes(preferredLanguage))) {
            await changeLanguageInOrder(i18n, preferredLanguage || 'en', () => !cancelled);
          }
        } else if (!isLoginRoute) {
          removeUser();
        }
        if (!cancelled && !isLoginRoute) initialized.current = true;
      } catch (error) {
        if (!cancelled) { console.error('App init error:', error); setInitError(error); }
      } finally {
        if (!cancelled) setLoad(false);
      }
    };
    initialize();
    return () => { cancelled = true; controller.abort(); };
  }, [i18n, retryCount, isLoginRoute, authenticatedFromLogin]);

  const retry = () => {
    setLoad(true);
    setInitError(null);
    setRetryCount(value => value + 1);
  };

  const routesItems = config.languages.map((l) =>
      <React.Fragment key={l}>
          <Route path={`${l}${APP_ROUTES.MAIN}`} element={<ReactionLine/> } />
          <Route path={`${l}/unsubscribe`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.SEARCH}`} element={<Search />} />
          <Route path={`${l}/notifications`} element={<NotificationsPage />} />
          <Route path={`${l}${APP_ROUTES.LOGIN}`} element={<Login />} />
          <Route path={`${l}${APP_ROUTES.POST}/:storyId/statistic`} element={<StoryStatistic />} />
          <Route path={`${l}${APP_ROUTES.POST}/:storyId/reactions`} element={<StoryUsers />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/:id`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.REACTION}/:id`} element={<ReactionLine />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/:id/report`} element={<ReactionReport />} />
          <Route path={`${l}${APP_ROUTES.REACTION}/opinion/:reactionId`} element={<ReactionEditor />} />
          <Route path={`${l}${APP_ROUTES.POST}/:id${APP_ROUTES.REACTION_EDIT}`} element={<ReactionEditor />} />
          <Route path={`${l}${APP_ROUTES.STORY_ADD}`} element={<StoryAdd />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username`} element={<UserProfile />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.USER_FOLLOWERS}`} element={<UserFollower type="followers" />} />
          <Route path={`${l}${APP_ROUTES.USER}/:username${APP_ROUTES.USER_FOLLOWING}`} element={<UserFollower type="following" />} />
          <Route path={`${l}${APP_ROUTES.USER_SETTINGS}`} element={<UserSettings />} />
          <Route path={`${l}${APP_ROUTES.USER_CONTROL}`} element={<UserControl />} />
          <Route path={`${l}${APP_ROUTES.PAGE404}`} element={<Page404 />} />
          <Route path={`${l}${APP_ROUTES.PAGE403}`} element={<Page403 />} />
          <Route path={`${l}${APP_ROUTES.STATIC}/:url`} element={<Static />} />
      </React.Fragment>
  );

  return (
    <ConfigProvider
      wave={{ disabled: true }}
      theme={{
        token: {
          fontFamily: "'Source Sans Pro', system-ui, -apple-system, 'Segoe UI', sans-serif",
          fontSize: 18,
          colorBgBase: varCss.surface,
          colorBgContainer: varCss.surface,
          colorBgElevated: varCss.surface,
          colorBgLayout: varCss.surface,
          colorWhite: varCss.surface,
          colorTextLightSolid: varCss.surface,
          // Pointer feedback must not paint transient grey/blue fills. Actual
          // selected states retain their own component/business tokens.
          colorBgTextHover: 'transparent',
          colorBgTextActive: 'transparent',
          controlItemBgHover: 'transparent',
          controlHeight: 48,
          controlHeightLG: 48,
          lineHeight: 4 / 3,
          activeBorderColor: varCss['color-black'],
          activeShadow: 'none',
          hoverBorderColor: varCss['color-black'],
          colorPrimary: varCss['color-primary'],
          borderRadius: uiRadius,
          borderRadiusLG: uiRadius,
          borderRadiusSM: uiRadius,
          borderRadiusXS: uiRadius,
          lineWidth: 2,
          colorBorder: varCss['color-border'],
          colorPrimaryActive: varCss['color-primary']
        },
        components: {
          Input: {
            hoverBorderColor: varCss['color-border'],
            activeBorderColor: varCss['color-border'],
            activeShadow: 'none',
          },
          Radio: {
            colorPrimaryBorder: varCss['color-border'],
            buttonSolidCheckedBg: varCss['color-border']
          },
          Select: {
            singleItemHeightLG: 'auto',
            optionActiveBg: 'transparent',
          },
          Button: {
            onlyIconSize: '24px',
            defaultColor: varCss['color-primary'],
            defaultHoverBg: varCss.surface,
            defaultActiveBg: varCss.surface,
            defaultActiveBorderColor: varCss['color-border'],
            defaultGhostBorderColor: varCss['color-border'],
            ghostBg: 'transparent',
            textHoverBg: 'transparent',
            colorBgTextActive: 'transparent',
            colorPrimaryHover: varCss['color-primary'],
            colorPrimaryActive: varCss['color-primary'],
            colorPrimaryTextHover: 'green',
            colorPrimaryBorder: 'red',
            colorPrimaryBorderHover: varCss['color-border'],
            controlOutline: 'transparent',
            controlOutlineWidth: 0
          }
        }
      }}
    >
      <>
        <MessageLayout />
        <AppUrlListener />
        <AppInstallBanner suppressed={!entryReady || onboardingEligible} />
        <Helmet>
          <title>WIW</title>
          <meta name="description" content="Who is Who" />
        </Helmet>
        <NotificationsProvider ready={!load && !initError && !isLoginRoute && (initialized.current || authenticatedFromLogin)}>
        <InAppProvider ready={!load && !initError && !isLoginRoute && (initialized.current || authenticatedFromLogin)}>
        {!entryReady || load || (!isLoginRoute && !initialized.current && !authenticatedFromLogin && !initError)
          ? <BrandLoader fullPage label={t('loading')} /> : initError ?
          <AppFailure onRetry={retry} /> :
          <Routes>{routesItems}<Route path="*" element={<Page404 />} /></Routes>}
        </InAppProvider>
        </NotificationsProvider>
      </>
    </ConfigProvider>
  );
}

export default function App() {
  return <BrowserRouter><ErrorBoundary><OnboardingProvider><AppContent /></OnboardingProvider></ErrorBoundary></BrowserRouter>;
}
